import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

type SortableMatterStatusRowProps = {
  canReorder: boolean;
  children: ReactNode;
  id: string;
  isReordering: boolean;
  label: string;
};

export function SortableMatterStatusRow({
  canReorder,
  children,
  id,
  isReordering,
  label,
}: SortableMatterStatusRowProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: !canReorder || isReordering });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "group hover:bg-light-primary/50 -mx-4 flex items-start gap-2 px-4 py-5 transition-colors sm:-mx-6 sm:gap-3 sm:px-6",
        isDragging && "bg-background relative z-10 shadow-sm",
      )}
    >
      {canReorder ? (
        <button
          type="button"
          className={cn(
            "text-muted-foreground mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
            isReordering
              ? "cursor-not-allowed opacity-50"
              : "bg-background hover:bg-muted cursor-grab active:cursor-grabbing",
          )}
          aria-label={`Reorder ${label}`}
          disabled={isReordering}
          onClick={(event) => {
            event.stopPropagation();
          }}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      {children}
    </div>
  );
}
